import { useEffect, useState, useCallback } from 'react';
import styles from './CommunityPage.module.css';
import { useAuth } from '../contexts/AuthContext';
import { useCity } from '../contexts/CityContext';
import { 
  createCommunityPost, 
  listCommunityPosts, 
  addCommentToPost, 
  listCommentsForPost, 
  likeCommunityPost,
  deleteCommunityPost,
  updateCommunityPost,
  listCommunityVisibleUsers,
  getOrCreateConversation
} from '../services/firebase';
import MessagingPanel from '../components/community/MessagingPanel';
import { CommunityPost, CommunityComment } from '../types/community';

interface EnrichedPost extends CommunityPost { comments?: CommunityComment[]; commentsLoaded?: boolean; loadingComments?: boolean; addingComment?: boolean; newComment?: string; liking?: boolean; editing?: boolean; editContent?: string; editTags?: string; savingEdit?: boolean; deleting?: boolean; }

function CommunityPage(){
  const { isLoggedIn, userProfile } = useAuth();
  const [showMessages, setShowMessages] = useState(false);
  const [startWithUserId, setStartWithUserId] = useState<string|undefined>(undefined);
  const [activeConversationId, setActiveConversationId] = useState<string|undefined>(undefined);
  const { currentCity } = useCity();
  const [posts, setPosts] = useState<EnrichedPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState<string|null>(null);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadPosts = useCallback(async (reset=false) => {
    try {
      setLoading(true);
      const res = await listCommunityPosts({ pageSize:10, lastDoc: reset? null : lastDoc, cityId: currentCity?.id });
      setPosts(p => reset ? res.items : [...p, ...res.items]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch(e:any){ setError(e.message||'Postlar alınamadı'); }
    finally { setLoading(false); }
  }, [lastDoc, currentCity?.id]);

  useEffect(()=>{ 
    // Şehir değiştiğinde önce mevcut listeyi temizle ki eski şehir postları gözükmesin
    setPosts([]); setLastDoc(null); setHasMore(false);
    if (currentCity?.id) { loadPosts(true); }
  }, [currentCity?.id, loadPosts]);

  const handleCreate = async () => {
    if (!isLoggedIn) { setError('Gönderi oluşturmak için giriş yap.'); return; }
    if (!content.trim()) return;
    try {
      setCreating(true);
      const tagArr = tags.split(',').map(t=>t.trim()).filter(Boolean).slice(0,6);
  await createCommunityPost({ content: content.trim(), tags: tagArr, cityId: currentCity?.id, cityCodeFallback: currentCity?.cityCode });
      setContent(''); setTags('');
      await loadPosts(true);
    } catch(e:any){ setError(e.message||'Gönderi eklenemedi'); }
    finally { setCreating(false); }
  };

  const loadComments = async (postId: string) => {
    setPosts(ps => ps.map(p => p.id === postId ? { ...p, loadingComments:true } : p));
    try {
      const res = await listCommentsForPost(postId, { pageSize: 20 });
      setPosts(ps => ps.map(p => p.id === postId ? { ...p, comments: res.items, commentsLoaded:true, loadingComments:false } : p));
    } catch(e){
      setPosts(ps => ps.map(p => p.id === postId ? { ...p, loadingComments:false } : p));
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!isLoggedIn) { setError('Yorum yapmak için giriş yap.'); return; }
    setPosts(ps => ps.map(p => p.id === postId ? { ...p, addingComment:true } : p));
    const post = posts.find(p=>p.id===postId);
    const text = post?.newComment?.trim();
    if(!text) { setPosts(ps => ps.map(p => p.id === postId ? { ...p, addingComment:false } : p)); return; }
    try {
      await addCommentToPost(postId, text);
      await loadComments(postId);
      setPosts(ps => ps.map(p => p.id === postId ? { ...p, newComment:'', addingComment:false } : p));
    } catch(e){
      setPosts(ps => ps.map(p => p.id === postId ? { ...p, addingComment:false } : p));
    }
  };

  const handleLike = async (postId: string) => {
    setPosts(ps => ps.map(p => p.id === postId ? { ...p, liking:true } : p));
    try { await likeCommunityPost(postId); await loadPosts(true); } finally { setPosts(ps => ps.map(p => p.id === postId ? { ...p, liking:false } : p)); }
  };

  // kullanıcı listesi
  useEffect(()=>{ (async()=>{ setLoadingUsers(true); try { const list = await listCommunityVisibleUsers(80); setUsers(list); } finally { setLoadingUsers(false); } })(); }, []);

  return (
    <div className={styles.communityPage} style={{display:'grid', gridTemplateColumns:'minmax(0,1fr) 250px', gap:'1.25rem', alignItems:'flex-start'}}>
      <div>
      <div className={styles.headerBar}>
        <h1 className={styles.title}>Topluluk</h1>
        {isLoggedIn && (
          <button className={styles.actionBtn} style={{fontSize:'.6rem'}} onClick={()=>{ setShowMessages(s=>!s); setStartWithUserId(undefined); }}>
            {showMessages? 'Mesajları Gizle':'Mesajlar'}
          </button>
        )}
      </div>
      {error && <div style={{fontSize:'.65rem', color:'var(--danger-color,#ff6b6b)', background:'rgba(255,80,80,.12)', border:'1px solid rgba(255,80,80,.35)', padding:'.6rem .8rem', borderRadius:'16px'}}>{error}</div>}
      <div className={styles.newPostCard}>
        <textarea
          className={styles.postTextarea}
          placeholder={isLoggedIn ? 'Toplulukla bir şey paylaş...' : 'Gönderi yazmak için giriş yap.'}
          disabled={!isLoggedIn || creating}
          value={content}
          onChange={e=>setContent(e.target.value)}
        />
        <div className={styles.postActions}>
          <input
            className={styles.tagInput}
            placeholder="Etiketler (virgülle ayır)"
            disabled={!isLoggedIn || creating}
            value={tags}
            onChange={e=>setTags(e.target.value)}
          />
          <button
            className={styles.publishBtn}
            disabled={!isLoggedIn || creating || !content.trim()}
            onClick={handleCreate}
            onMouseMove={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); const mx=((e.clientX-r.left)/r.width)*100; const my=((e.clientY-r.top)/r.height)*100; e.currentTarget.style.setProperty('--mx',mx+'%'); e.currentTarget.style.setProperty('--my',my+'%'); }}
            onMouseLeave={(e)=>{ e.currentTarget.style.setProperty('--mx','50%'); e.currentTarget.style.setProperty('--my','50%'); }}
          >{creating? 'Paylaşılıyor...' : 'Paylaş'}</button>
        </div>
      </div>
      <div className={styles.postList}>
        {posts.map(post => (
          <div key={post.id} className={styles.postCard}>
            <div className={styles.postHead}>
              <div className={styles.avatar} title="Mesaj gönder" style={{cursor: isLoggedIn? 'pointer':'default'}} onClick={async ()=>{
                if(!isLoggedIn || !userProfile) return; if (post.authorId === userProfile.uid) return; 
                try { const convId = await getOrCreateConversation(post.authorId); setActiveConversationId(convId); setStartWithUserId(undefined); setShowMessages(true); } catch(e:any){ setError(e.message||'Sohbet başlatılamadı'); }
              }}>
                {(() => {
                  const vis = (post as any).authorAvatarVisibility || 'everyone';
                  const canShow = (() => {
                    if (vis === 'everyone') return true;
                    if (vis === 'members' && userProfile) return true;
                    // owner case: if current user is author, always show
                    if (post.authorId === userProfile?.uid) return true;
                    return false;
                  })();
                  if (canShow && post.authorPhotoURL) return <img src={post.authorPhotoURL} alt={post.authorDisplayName} />;
                  return (post.authorDisplayName||'K')[0].toUpperCase();
                })()}
              </div>
              <div className={styles.meta}>
                <div className={styles.authorLine}>{post.authorDisplayName} {post.authorUsername && <span style={{opacity:.6}}>@{post.authorUsername}</span>}</div>
                <div className={styles.timeLine}>{new Date(post.createdAt).toLocaleString()}</div>
              </div>
              {(userProfile && (userProfile.uid === post.authorId || userProfile.role === 'admin')) && !post.editing && (
                <div style={{marginLeft:'auto', display:'flex', gap:'.4rem'}}>
                  <button className={styles.actionBtn} style={{fontSize:'.55rem'}} onClick={()=> setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, editing:true, editContent:p.content, editTags:(p.tags||[]).join(', ')} : p))}>Düzenle</button>
                  <button className={styles.actionBtn} style={{fontSize:'.55rem'}} disabled={post.deleting} onClick={async ()=>{
                    if(!confirm('Gönderi silinsin mi? Bu işlem geri alınamaz.')) return;
                    setPosts(ps=>ps.map(p=>p.id===post.id? {...p, deleting:true}:p));
                    try { await deleteCommunityPost(post.id); setPosts(ps=> ps.filter(p=>p.id!==post.id)); } catch(e:any){ setError(e.message||'Silinemedi'); setPosts(ps=>ps.map(p=>p.id===post.id? {...p, deleting:false}:p)); }
                  }}>Sil</button>
                </div>
              )}
            </div>
            {!post.editing && <div className={styles.content}>{post.content}</div>}
            {post.editing && (
              <div style={{display:'flex', flexDirection:'column', gap:'.5rem'}}>
                <textarea
                  style={{width:'100%', minHeight:'80px', resize:'vertical', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.18)', borderRadius:'14px', padding:'.6rem .75rem', fontSize:'.7rem', color:'var(--text-color,#fff)'}}
                  value={post.editContent}
                  onChange={e=> setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, editContent:e.target.value} : p))}
                  disabled={post.savingEdit}
                />
                <input
                  style={{width:'100%', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.18)', borderRadius:'14px', padding:'.5rem .75rem', fontSize:'.6rem', color:'var(--text-color,#fff)'}}
                  placeholder="Etiketler (virgülle)"
                  value={post.editTags || ''}
                  onChange={e=> setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, editTags:e.target.value} : p))}
                  disabled={post.savingEdit}
                />
                <div style={{display:'flex', gap:'.5rem', flexWrap:'wrap'}}>
                  <button className={styles.actionBtn} style={{fontSize:'.55rem'}} disabled={post.savingEdit} onClick={()=> setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, editing:false} : p))}>İptal</button>
                  <button className={styles.actionBtn} style={{fontSize:'.55rem'}} disabled={post.savingEdit || !(post.editContent||'').trim()} onClick={async ()=>{
                    setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, savingEdit:true} : p));
                    try {
                      const tagArr = (post.editTags||'').split(',').map(t=>t.trim()).filter(Boolean).slice(0,8);
                      await updateCommunityPost(post.id, { content: post.editContent||'', tags: tagArr });
                      setPosts(ps=> ps.map(p=> p.id===post.id ? { ...p, content: post.editContent||'', tags: tagArr, editing:false, savingEdit:false } : p));
                    } catch(e:any){ setError(e.message||'Güncellenemedi'); setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, savingEdit:false} : p)); }
                  }}>{post.savingEdit? 'Kaydediliyor...' : 'Kaydet'}</button>
                </div>
              </div>
            )}
            {!post.editing && post.tags && post.tags.length>0 && (
              <div className={styles.tagRow}>{post.tags.map(t=> <span key={t} className={styles.tag}>#{t}</span>)}</div>
            )}
            {!post.editing && (
              <div className={styles.actionsRow}>
                <button className={styles.actionBtn} disabled={post.liking} onClick={()=>handleLike(post.id)} onMouseMove={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); const mx=((e.clientX-r.left)/r.width)*100; const my=((e.clientY-r.top)/r.height)*100; e.currentTarget.style.setProperty('--mx',mx+'%'); e.currentTarget.style.setProperty('--my',my+'%'); }}>👍 {post.likesCount}</button>
                <button className={styles.actionBtn} onClick={()=> post.commentsLoaded ? setPosts(ps=>ps.map(p=>p.id===post.id? {...p, commentsLoaded:false, comments:[]} : p)) : loadComments(post.id)} onMouseMove={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); const mx=((e.clientX-r.left)/r.width)/1*100; const my=((e.clientY-r.top)/r.height)*100; e.currentTarget.style.setProperty('--mx',mx+'%'); e.currentTarget.style.setProperty('--my',my+'%'); }}>💬 {post.commentsCount}</button>
              </div>
            )}
            {post.loadingComments && <div style={{fontSize:'.6rem', opacity:.6}}>Yorumlar yükleniyor...</div>}
            {post.commentsLoaded && (
              <div className={styles.commentSection}>
                {post.comments && post.comments.length>0 ? post.comments.map(c => (
                  <div key={c.id} className={styles.commentItem}>
                    <div className={styles.commentAvatar}>{(() => {
                      const vis = (c as any).avatarVisibility || 'everyone';
                      const canShow = (() => {
                        if (c.userId === userProfile?.uid) return true;
                        if (vis === 'everyone') return true;
                        if (vis === 'members' && userProfile) return true;
                        return false;
                      })();
                      if (canShow && c.photoURL) return <img src={c.photoURL} alt={c.displayName} style={{width:'100%',height:'100%',objectFit:'cover'}} />;
                      return c.displayName[0].toUpperCase();
                    })()}</div>
                    <div className={styles.commentBody}>
                      <div className={styles.commentMeta}>{c.displayName} · {new Date(c.createdAt).toLocaleTimeString()}</div>
                      <div className={styles.commentText}>{c.content}</div>
                    </div>
                  </div>
                )) : <div className={styles.emptyState} style={{padding:'0.5rem 0'}}>İlk yorumu sen yaz.</div>}
                <div className={styles.addCommentRow}>
                  <textarea
                    className={styles.commentInput}
                    placeholder={isLoggedIn? 'Yorum ekle...' : 'Yorum için giriş yap'}
                    disabled={!isLoggedIn || post.addingComment}
                    value={post.newComment || ''}
                    onChange={e=> setPosts(ps=> ps.map(p=> p.id===post.id ? {...p, newComment:e.target.value} : p))}
                  />
                  <button
                    className={styles.addCommentBtn}
                    disabled={!isLoggedIn || post.addingComment || !(post.newComment||'').trim()}
                    onClick={()=>handleAddComment(post.id)}
                    onMouseMove={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); const mx=((e.clientX-r.left)/r.width)*100; const my=((e.clientY-r.top)/r.height)*100; e.currentTarget.style.setProperty('--mx',mx+'%'); e.currentTarget.style.setProperty('--my',my+'%'); }}
                    onMouseLeave={(e)=>{ e.currentTarget.style.setProperty('--mx','50%'); e.currentTarget.style.setProperty('--my','50%'); }}
                  >{post.addingComment? 'Gönderiliyor...' : 'Gönder'}</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!loading && posts.length===0 && <div className={styles.emptyState}>Henüz gönderi yok.</div>}
        {hasMore && <div className={styles.loadMoreWrap}><button className={styles.loadMoreBtn} disabled={loading} onClick={()=>loadPosts(false)} onMouseMove={(e)=>{ const r=e.currentTarget.getBoundingClientRect(); const mx=((e.clientX-r.left)/r.width)*100; const my=((e.clientY-r.top)/r.height)*100; e.currentTarget.style.setProperty('--mx',mx+'%'); e.currentTarget.style.setProperty('--my',my+'%'); }}>{loading? 'Yükleniyor...' : 'Daha Fazla'}</button></div>}
        {loading && <div style={{fontSize:'.65rem', opacity:.6, textAlign:'center'}}>Yükleniyor...</div>}
      </div>
  {showMessages && <MessagingPanel startWithUserId={startWithUserId} conversationId={activeConversationId} onClose={()=> setShowMessages(false)} />}
      </div>
      <aside style={{position:'sticky', top:'calc(var(--header-mobile-height,70px) + 1rem)', display:'flex', flexDirection:'column', gap:'1rem'}}>
        <div style={{background:'var(--glass-background)', border:'var(--glass-border)', backdropFilter:'blur(var(--glass-blur))', borderRadius:'24px', padding:'.9rem .95rem 1.1rem', display:'flex', flexDirection:'column', gap:'.7rem', maxHeight:'70vh'}}>
          <div style={{fontSize:'.72rem', fontWeight:600, letterSpacing:'.5px'}}>Kullanıcılar</div>
          {loadingUsers && <div style={{fontSize:'.55rem', opacity:.6}}>Yükleniyor...</div>}
          {!loadingUsers && users.length===0 && <div style={{fontSize:'.55rem', opacity:.6}}>Kullanıcı yok</div>}
          <div style={{display:'flex', flexDirection:'column', gap:'.55rem', overflowY:'auto', paddingRight:'.25rem'}}>
            {users.map(u => (
              <div key={u.uid} style={{display:'flex', alignItems:'center', gap:'.55rem'}}>
                <div style={{width:'36px', height:'36px', borderRadius:'50%', background:'linear-gradient(135deg,var(--primary-color),#6e4bff)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.65rem', fontWeight:600}}>
                  {u.photoURL ? <img src={u.photoURL} alt={u.displayName} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : (u.displayName||'K').charAt(0).toUpperCase()}
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:'.6rem', fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{u.displayName}</div>
                  {u.username && <div style={{fontSize:'.5rem', opacity:.55, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>@{u.username}</div>}
                </div>
                {isLoggedIn && userProfile?.uid !== u.uid && (
                  <button style={{fontSize:'.5rem', background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.25)', padding:'.35rem .55rem', borderRadius:'14px', cursor:'pointer'}} onClick={async ()=>{ 
                    try { const convId = await getOrCreateConversation(u.uid); setActiveConversationId(convId); setStartWithUserId(undefined); setShowMessages(true); } catch(e:any){ setError(e.message||'Sohbet başlatılamadı'); }
                  }}>Mesaj</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default CommunityPage;
